import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthenticationService } from '../../api/generated/services/AuthenticationService';
import { AuthView } from '../../api/generated/models/AuthView';
import { fetchProfile, updateContactData } from './profileSlice';
import { fetchDataSource } from './dataSourceSlice';
import { fetchUserLicenses } from './licenseSlice';
import { fetchContentMasterWorkspaceConfig } from './contentMasterSlice';
import { initializeOpenAPI } from '../../config/openapi';
import { resetLogoutFlag } from '../../services/auth';
import { helper } from '../../utils/helper';
import { isDataSourceConfigured } from '../../utils/dataSourceHelpers';
import type { RootState } from '../store';

const isAuth = (): boolean => {
  const token = localStorage.getItem('token');
  return !!token;
};

interface AuthUser {
  userId: string;
  email: string;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isOpenAPIReady: boolean;
  isDataInitialized: boolean;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  isAuthenticated: isAuth(),
  isOpenAPIReady: false,
  isDataInitialized: false,
};

interface LoginParams {
  email: string;
  password: string;
  twoFactorCode?: string;
  captchaToken?: string | null;
}

/** Sentinel rejectValue for 422 "Captcha verification failed." responses. */
export const CAPTCHA_FAILED = 'captcha_failed';

type LoginResult = {
  type: 'success';
  data: AuthView;
} | {
  type: 'two_factor_required';
}

// Login thunk
export const loginUser = createAsyncThunk<LoginResult, LoginParams, { rejectValue: string }>(
  'auth/login',
  async ({ email, password, twoFactorCode, captchaToken }, { rejectWithValue, dispatch }) => {
    try {
      const response = await AuthenticationService.postAppAuthLogin({
        email,
        password,
        twoFactorCode: twoFactorCode || null,
        captchaToken: captchaToken || null,
      });
      
      // Save token to localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify({
        userId: response.userId,
        email: response.user?.email || email,
      }));
      
      // Update profile if user data is available
      if (response.user) {
        dispatch(updateContactData(response.user));
      }
      
      return { type: 'success' as const, data: response };
    } catch (error: any) {
      if (error?.status === 403) {
        const body = error?.body;
        if (body?.requiresTwoFactor) {
          return { type: 'two_factor_required' as const };
        }
      }
      if (error?.status === 422) {
        return rejectWithValue(CAPTCHA_FAILED);
      }
      const errorMessage = error?.body?.error || error?.message || 'Login failed';
      return rejectWithValue(errorMessage);
    }
  }
);

// Register thunk
export const registerUser = createAsyncThunk<AuthView, { email: string; password: string; referralCode?: string | null; captchaToken?: string | null }, { rejectValue: string }>(
  'auth/register',
  async ({ email, password, referralCode, captchaToken }, { rejectWithValue, dispatch }) => {
    try {
      // Invalid referral codes are ignored silently by the backend - no
      // client-side error handling needed (per the affiliate contract).
      const response = await AuthenticationService.postAppAuthRegister({
        email,
        password,
        referralCode: referralCode || null,
        captchaToken: captchaToken || null,
      });
      
      // Save token to localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify({
        userId: response.userId,
        email: response.user?.email || email,
      }));
      
      // Update profile if user data is available
      if (response.user) {
        dispatch(updateContactData(response.user));
      }
      
      return response;
    } catch (error: any) {
      if (error?.status === 422) {
        return rejectWithValue(CAPTCHA_FAILED);
      }
      const errorMessage = error?.body?.error || error?.message || 'Registration failed';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Module-level guard preventing concurrent / repeated bootstrap dispatches.
 * Reset by `logout` so a subsequent login can re-bootstrap. The accompanying
 * thunk `condition` short-circuits before any side effects run.
 */
let bootstrapInFlight = false;
let bootstrapCompleted = false;

/**
 * Centralized one-shot bootstrap that loads the data every authenticated page
 * relies on (user, accepted-date, ds, subscriptions) plus, conditionally,
 * the Content Master workspace config. Called once on app start (when a token
 * exists) and once after every successful login / register / reset / activate.
 *
 * The helper is intentionally idempotent: a `condition` guard drops the
 * dispatch when the bootstrap is already in flight or has completed in this
 * session. `logout` resets the guard so a re-login re-bootstraps.
 */
export const bootstrapSession = createAsyncThunk<boolean, void, { state: RootState }>(
  'auth/bootstrapSession',
  async (_, { dispatch, getState }) => {
    bootstrapInFlight = true;
    try {
      // Run profile, ds and subscriptions in parallel - none of them depend on
      // each other. `silent: true` for licenses suppresses the global loading
      // bar (the bootstrap is already gating the entire UI).
      await Promise.allSettled([
        dispatch(fetchProfile()),
        dispatch(fetchDataSource()),
        dispatch(fetchUserLicenses({ page: 1, pageSize: 100, silent: true })),
      ]);

      // Workspace config depends on both an active CM subscription AND a
      // configured JTL data source - calling it without either would return
      // `content_master_wawi_connection_failed`.
      const state = getState();
      const subscriptions = state.licenses.items;
      const hasActiveContentMaster = subscriptions.some(
        (s) => s.pluginName === 'content_master' && helper.isSubscriptionActive(s)
      );
      const dsReady = isDataSourceConfigured(state.dataSource.data);
      // `workspaceMissing` guards against a duplicate request: `fetchUserLicenses`
      // above may already have loaded the workspace config (it runs the same
      // conditional fetch). Without this check the config would be requested
      // twice on every authenticated app start.
      const workspaceMissing = state.contentMaster.workspaceConfig == null;
      if (hasActiveContentMaster && dsReady && workspaceMissing) {
        await dispatch(fetchContentMasterWorkspaceConfig(undefined));
      }

      bootstrapCompleted = true;
      return true;
    } catch (error) {
      console.error('Failed to bootstrap session:', error);
      return false;
    } finally {
      bootstrapInFlight = false;
    }
  },
  {
    condition: () => {
      if (bootstrapInFlight) return false;
      if (bootstrapCompleted) return false;
      return true;
    },
  }
);

/**
 * Single post-auth handshake used by every code path that turns an
 * `AuthView` (login / register / reset / activate response) into an
 * authenticated session: persists the token, primes OpenAPI + Redux, then
 * runs the bootstrap. Returning a thunk keeps the auth pages free of any
 * "where do I dispatch what" branching.
 */
export const completeAuthSession = createAsyncThunk<void, AuthView, { state: RootState }>(
  'auth/completeSession',
  async (response, { dispatch }) => {
    const userPayload: AuthUser = {
      userId: response.userId,
      email: response.user?.email || '',
    };

    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(userPayload));

    initializeOpenAPI(response.token);
    dispatch(setOpenAPIReady(true));
    dispatch(setUser(userPayload));
    dispatch(setIsAuthenticated(true));
    if (response.user) {
      dispatch(updateContactData(response.user));
    }
    resetLogoutFlag();

    const ok = await dispatch(bootstrapSession()).unwrap();
    if (ok) {
      dispatch(setDataInitialized(true));
    }
  }
);

/**
 * @deprecated Use `bootstrapSession` directly. Kept temporarily so existing
 * call sites compile while migration finishes; removes after the App.tsx
 * collapse lands.
 */
export const initializeUserData = bootstrapSession;

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<AuthUser | null>) => {
      state.user = action.payload;
      state.error = null;
    },
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setOpenAPIReady: (state, action: PayloadAction<boolean>) => {
      state.isOpenAPIReady = action.payload;
    },
    setDataInitialized: (state, action: PayloadAction<boolean>) => {
      state.isDataInitialized = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    logout: (state) => {
      // Clear auth data from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      // Reset bootstrap guard so a subsequent login re-bootstraps.
      bootstrapInFlight = false;
      bootstrapCompleted = false;

      // Reset state
      state.user = null;
      state.isAuthenticated = false;
      state.isOpenAPIReady = false;
      state.isDataInitialized = false;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.type === 'success') {
          state.isAuthenticated = true;
          state.user = {
            userId: action.payload.data.userId,
            email: action.payload.data.user?.email || '',
          };
        }
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Login failed';
        state.isAuthenticated = false;
      });

    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = {
          userId: action.payload.userId,
          email: action.payload.user?.email || '',
        };
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Registration failed';
        state.isAuthenticated = false;
      });
  },
});

export const { 
  setUser, 
  setLoading, 
  setError, 
  logout, 
  setIsAuthenticated, 
  setOpenAPIReady, 
  setDataInitialized 
} = authSlice.actions;

export default authSlice.reducer;
